<template>
  <div v-if="isLoading" id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  <template v-else-if="!isLoading">
    <div class="public-projects">
      <div class="hero-banner">
        <div class="container hero-container">
          <div class="hero-content">
            <h1>Explore Public Projects</h1>
            <span>Browse and clone public projects to power your projects and experiments.</span>
            <div class="hero-search">
              <v-text-field
                v-model="searchValue"
                clearable
                label=""
                variant="outlined"
                placeholder="Search projects..."
                hide-details
              />
            </div>
          </div>
          <div v-if="!isMobile" class="hero-image">
            <img src="@/assets/img/explore-datasets.png" alt="Explore image projects">
          </div>
        </div>
      </div>
      <div v-if="isLoadingSearch || publicProjectsSearch" class="explore-projects-container">
        <div v-if="isLoadingSearch" id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
        <template v-else>
          <h2 class="section-heading">Search Results</h2>
          <div class="card-container scrollbar">
            <PublicProjectCard
              v-for="p in publicProjectsSearch"
              :ref="`projectCard${p.id}`"
              :key="p.id"
              :project="p"
              :showMenu="user"
              @clone-project="handleCloneProject"
            />
          </div>
        </template>
      </div>
      <div v-else class="explore-projects-container">
        <h2 class="section-heading">
          <SVGIcon
            :iconName="'trending_up'"
            :width="'24px'"
            :height="'24px'"
          />Trending Projects
        </h2>
        <div class="card-container scrollbar">
          <PublicProjectCard
            v-for="p in publicProjectsTrending"
            :ref="`projectCard${p.id}`"
            :key="p.id"
            :project="p"
            :showMenu="user"
            @clone-project="handleCloneProject"
          />
        </div>
        <hr class="section-hr">
        <h2 class="section-heading">Most Recent Projects</h2>
        <div class="card-container scrollbar">
          <PublicProjectCard
            v-for="p in publicProjectsNewest"
            :ref="`projectCard${p.id}`"
            :key="p.id"
            :project="p"
            :showMenu="user"
            @clone-project="handleCloneProject"
          />
        </div>
      </div>
    </div>
  </template>
</template>

<script setup>
import {
  ref, watch, computed, onMounted,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import { useMobile } from '@/composables/useMobile';
import PublicProjectCard from "@/components/PublicProjects/PublicProjectCard.vue";
import SVGIcon from '@/components/SVGIcon.vue';
import * as utils from '@/assets/js/utils.js';
import { useStore } from 'vuex';
import { event } from 'vue-gtag';

const $toast = useToast();
const { isMobile } = useMobile();
const store = useStore();

const isLoading = ref(false);
const isLoadingSearch = ref(false);
const publicProjectsNewest = ref([]);
const publicProjectsTrending = ref([]);
const publicProjectsSearch = ref(null);
const user = computed(() => store.state.user.user);

const searchValue = ref('');

async function getProjectList(params = {}) {
  const dataConnect = new DatastoreConnect();
  const result = await dataConnect.getProjectFilteredList(params)
    .then(async (data) => {
      if (!data.result) {
        throw data.error.message;
      }
      return data.result;
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to get project list: ${error}`,
        type: "error",
        position: "top",
      });
    });

  // let noLT = 0;
  // let noDS = 0;
  // let noTR = 0;
  // let noVA = 0;
  // let noAU = 0;

  // publicProjects.value.forEach((proj) => {
  //   noAU += proj.no_of_automations;
  //   noLT += proj.no_of_label_tasks;
  //   noDS += proj.no_of_datasets;
  //   noTR += proj.no_of_trainers;
  //   noVA += proj.no_of_validations;
  // });

  // for (let k = 0; k < publicProjects.value.length; k++) {
  //   publicProjects.value[k].total_automations = noAU;
  //   publicProjects.value[k].total_datasets = noDS;
  //   publicProjects.value[k].total_trainers = noTR;
  //   publicProjects.value[k].total_validations = noVA;
  //   publicProjects.value[k].total_label_tasks = noLT;
  // }

  return result;
}

async function handleProjectsSearch(searchValue) {
  if (!searchValue) {
    // if cleared, reset to trending/newest
    publicProjectsSearch.value = null;
    isLoadingSearch.value = false;
    return;
  }

  isLoadingSearch.value = true;
  event('search_public_project', {
    category: 'public_project',
    label: 'User searched for public project',
    value: searchValue,
  });
  publicProjectsSearch.value = await getProjectList({ search: searchValue });
  isLoadingSearch.value = false;
}

function clearSearch() {
  searchValue.value = '';
  publicProjectsSearch.value = null; // reset search results
}

async function handleCloneProject(id) {
  const dataConnect = new DatastoreConnect();
  const result = await dataConnect.cloneProject({ project_id: id })
    .then(async (data) => {
      if (!data.result) {
        throw data.error.message;
      }
      $toast.open({
        queue: true,
        dismissible: true,
        duration: 5000,
        message: `Successfully cloned project`,
        type: "success",
        position: "top",
      });
      return data.result;
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to clone project: ${error}`,
        type: "error",
        position: "top",
      });
    });
}

watch(searchValue, (newVal) => {
  debouncedHandleProjectsSearch(newVal);
});

const debouncedHandleProjectsSearch = utils.debounce(handleProjectsSearch, 600);

onMounted(async () => {
  isLoading.value = true;

  const trendingPromise = getProjectList({ trending: true, limit: 10 });
  const newestPromise = getProjectList({ newest: true, limit: 10 });

  const [trendingResult, newestResult] = await Promise.all([trendingPromise, newestPromise]);

  publicProjectsTrending.value = trendingResult;
  publicProjectsNewest.value = newestResult;

  isLoading.value = false;
});

</script>

<style lang="scss" scoped>
.public-projects {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow: auto;
}

.hero-banner {
  display: none;   //changed from flex
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  padding: 2.125rem 3rem;
  background: #f8f9fa;
  border-radius: 1rem;
}

@media (max-width: $breakpoint-sm) {
  .hero-banner {
    padding: 16px;
  }
}

.hero-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
}

.hero-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.hero-content h1 {
  font-size: 2.5rem;
  margin: 0;
}

.hero-content span {
  font-size: 1.1rem;
  color: #555;
  max-width: 500px;
}

.hero-search {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

.hero-search input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  font-size: 1rem;
}

.hero-search button {
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 0.5rem;
  background-color: #339af0;
  color: white;
  font-size: 1rem;
  cursor: pointer;
}

.hero-search button:hover {
  background-color: #1c7ed6;
}

.hero-image img {
  max-width: 250px;
  height: auto;
}

.explore-projects-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 64px;
  flex: 1 1 auto;
}

@media (max-width: $breakpoint-sm) {
  .explore-projects-container {
    padding: 16px;
  }
}

.card-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(320px, 100%), 1fr));
  grid-auto-rows: 120px;
  width: 100%;
  gap: 16px;
  // padding: 16px;
  // overflow-y: auto;
  // flex: 1 1 auto;
}

.section-hr {
  margin: 32px 0;
}

.section-heading {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: 600;
  font-size: 1.5rem;

  svg {
    margin-right: 8px;
    color: var(--color-primary);
  }
}
</style>
